// v2/config/workflows.config.ts — Workflow state machines as structured data
// Extracted from v1 permission matrices and application state transitions

import { Action, ApplicationState, ProductType, Role } from '../shared/types/domain';
import { WorkflowDefinition } from './types';

// ─── Personal Loan Workflow ──────────────────────────────────────────────────
// Includes PENDING_VERIFICATION and VERIFICATION_COMPLETE states

const PERSONAL_LOAN_WORKFLOW: WorkflowDefinition = {
  productType: ProductType.PERSONAL_LOAN,
  states: [
    ApplicationState.DRAFT,
    ApplicationState.SUBMITTED,
    ApplicationState.SOFT_APPROVED,
    ApplicationState.PENDING_VERIFICATION,
    ApplicationState.VERIFICATION_COMPLETE,
    ApplicationState.APPROVED,
    ApplicationState.MANDATED,
    ApplicationState.SIGNED,
    ApplicationState.DISBURSED,
    ApplicationState.REJECTED,
  ],
  transitions: [
    {
      from: ApplicationState.DRAFT,
      to: ApplicationState.SUBMITTED,
      allowedRoles: [Role.SALES_USER],
      action: Action.SUBMIT_APPLICATION,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.SOFT_APPROVED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SOFT_APPROVE,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.DRAFT,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.REQUEST_EDIT_APPLICATION,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.SOFT_APPROVED,
      to: ApplicationState.PENDING_VERIFICATION,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.START_VERIFICATION,
    },
    {
      from: ApplicationState.SOFT_APPROVED,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.VERIFICATION_COMPLETE,
      to: ApplicationState.APPROVED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SANCTION,
    },
    {
      from: ApplicationState.VERIFICATION_COMPLETE,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.APPROVED,
      to: ApplicationState.MANDATED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.MANDATE,
    },
    {
      from: ApplicationState.MANDATED,
      to: ApplicationState.SIGNED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SIGN,
    },
    {
      from: ApplicationState.SIGNED,
      to: ApplicationState.DISBURSED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.DISBURSE,
    },
    {
      from: ApplicationState.REJECTED,
      to: ApplicationState.DRAFT,
      allowedRoles: [Role.ADMIN],
      action: Action.REOPEN_APPLICATION,
    },
  ],
};

// ─── Business Loan Workflow ──────────────────────────────────────────────────
// Same as Personal Loan (includes verification states)

const BUSINESS_LOAN_WORKFLOW: WorkflowDefinition = {
  productType: ProductType.BUSINESS_LOAN,
  states: [
    ApplicationState.DRAFT,
    ApplicationState.SUBMITTED,
    ApplicationState.SOFT_APPROVED,
    ApplicationState.PENDING_VERIFICATION,
    ApplicationState.VERIFICATION_COMPLETE,
    ApplicationState.APPROVED,
    ApplicationState.MANDATED,
    ApplicationState.SIGNED,
    ApplicationState.DISBURSED,
    ApplicationState.REJECTED,
  ],
  transitions: [
    {
      from: ApplicationState.DRAFT,
      to: ApplicationState.SUBMITTED,
      allowedRoles: [Role.SALES_USER],
      action: Action.SUBMIT_APPLICATION,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.SOFT_APPROVED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SOFT_APPROVE,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.DRAFT,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.REQUEST_EDIT_APPLICATION,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.SOFT_APPROVED,
      to: ApplicationState.PENDING_VERIFICATION,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.START_VERIFICATION,
    },
    {
      from: ApplicationState.SOFT_APPROVED,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.VERIFICATION_COMPLETE,
      to: ApplicationState.APPROVED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SANCTION,
    },
    {
      from: ApplicationState.VERIFICATION_COMPLETE,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.APPROVED,
      to: ApplicationState.MANDATED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.MANDATE,
    },
    {
      from: ApplicationState.MANDATED,
      to: ApplicationState.SIGNED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SIGN,
    },
    {
      from: ApplicationState.SIGNED,
      to: ApplicationState.DISBURSED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.DISBURSE,
    },
    {
      from: ApplicationState.REJECTED,
      to: ApplicationState.DRAFT,
      allowedRoles: [Role.ADMIN],
      action: Action.REOPEN_APPLICATION,
    },
  ],
};

// ─── Consumer Durables Loan Workflow ─────────────────────────────────────────
// No PENDING_VERIFICATION / VERIFICATION_COMPLETE states
// Has INVOICE_COLLECTED between APPROVED and MANDATED

const CONSUMER_DURABLES_LOAN_WORKFLOW: WorkflowDefinition = {
  productType: ProductType.CONSUMER_DURABLES_LOAN,
  states: [
    ApplicationState.DRAFT,
    ApplicationState.SUBMITTED,
    ApplicationState.SOFT_APPROVED,
    ApplicationState.APPROVED,
    ApplicationState.INVOICE_COLLECTED,
    ApplicationState.MANDATED,
    ApplicationState.SIGNED,
    ApplicationState.DISBURSED,
    ApplicationState.REJECTED,
  ],
  transitions: [
    {
      from: ApplicationState.DRAFT,
      to: ApplicationState.SUBMITTED,
      allowedRoles: [Role.SALES_USER],
      action: Action.SUBMIT_APPLICATION,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.SOFT_APPROVED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SOFT_APPROVE,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.DRAFT,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.REQUEST_EDIT_APPLICATION,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.SOFT_APPROVED,
      to: ApplicationState.APPROVED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SANCTION,
    },
    {
      from: ApplicationState.SOFT_APPROVED,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.APPROVED,
      to: ApplicationState.INVOICE_COLLECTED,
      allowedRoles: [Role.SALES_USER],
      action: Action.INVOICE_COLLECTED,
    },
    {
      from: ApplicationState.INVOICE_COLLECTED,
      to: ApplicationState.MANDATED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.MANDATE,
    },
    {
      from: ApplicationState.MANDATED,
      to: ApplicationState.SIGNED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SIGN,
    },
    {
      from: ApplicationState.SIGNED,
      to: ApplicationState.DISBURSED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.DISBURSE,
    },
    {
      from: ApplicationState.REJECTED,
      to: ApplicationState.DRAFT,
      allowedRoles: [Role.ADMIN],
      action: Action.REOPEN_APPLICATION,
    },
  ],
};

// ─── Group Loan Workflow ─────────────────────────────────────────────────────
// Same as Business Loan (includes verification states)

const GROUP_LOAN_WORKFLOW: WorkflowDefinition = {
  productType: ProductType.GROUP_LOAN,
  states: [
    ApplicationState.DRAFT,
    ApplicationState.SUBMITTED,
    ApplicationState.SOFT_APPROVED,
    ApplicationState.PENDING_VERIFICATION,
    ApplicationState.VERIFICATION_COMPLETE,
    ApplicationState.APPROVED,
    ApplicationState.MANDATED,
    ApplicationState.SIGNED,
    ApplicationState.DISBURSED,
    ApplicationState.REJECTED,
  ],
  transitions: [
    {
      from: ApplicationState.DRAFT,
      to: ApplicationState.SUBMITTED,
      allowedRoles: [Role.SALES_USER],
      action: Action.SUBMIT_APPLICATION,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.SOFT_APPROVED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SOFT_APPROVE,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.DRAFT,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.REQUEST_EDIT_APPLICATION,
    },
    {
      from: ApplicationState.SUBMITTED,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.SOFT_APPROVED,
      to: ApplicationState.PENDING_VERIFICATION,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.START_VERIFICATION,
    },
    {
      from: ApplicationState.SOFT_APPROVED,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.VERIFICATION_COMPLETE,
      to: ApplicationState.APPROVED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SANCTION,
    },
    {
      from: ApplicationState.VERIFICATION_COMPLETE,
      to: ApplicationState.REJECTED,
      allowedRoles: [Role.BACK_OFFICE_USER, Role.ADMIN],
      action: Action.REJECT_APPLICATION,
      requiresReason: true,
    },
    {
      from: ApplicationState.APPROVED,
      to: ApplicationState.MANDATED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.MANDATE,
    },
    {
      from: ApplicationState.MANDATED,
      to: ApplicationState.SIGNED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.SIGN,
    },
    {
      from: ApplicationState.SIGNED,
      to: ApplicationState.DISBURSED,
      allowedRoles: [Role.BACK_OFFICE_USER],
      action: Action.DISBURSE,
    },
    {
      from: ApplicationState.REJECTED,
      to: ApplicationState.DRAFT,
      allowedRoles: [Role.ADMIN],
      action: Action.REOPEN_APPLICATION,
    },
  ],
};

// ─── Product-Type Workflow Map ────────────────────────────────────────────────

export const PRODUCT_TYPE_WORKFLOWS: Record<ProductType, WorkflowDefinition> = {
  [ProductType.PERSONAL_LOAN]: PERSONAL_LOAN_WORKFLOW,
  [ProductType.BUSINESS_LOAN]: BUSINESS_LOAN_WORKFLOW,
  [ProductType.CONSUMER_DURABLES_LOAN]: CONSUMER_DURABLES_LOAN_WORKFLOW,
  [ProductType.GROUP_LOAN]: GROUP_LOAN_WORKFLOW,
};